/**
 * The area filter, kept in the URL (RESP2e).
 *
 * ## Why the URL and not component state
 *
 * "Show me the open cleaning jobs" is a thing people send each other.
 * Before this slice no filter on either page survived a reload — the
 * report hook says so in its own comment: *"There is no URL or stored
 * filter for reports today; if one is ever added, it takes precedence
 * and this becomes the fallback."* This is that one.
 *
 * ## Scope, deliberately narrow
 *
 * ONLY the area filter moves to the URL. Status, priority, search,
 * assignee and the date range stay where they are. Migrating them is a
 * bigger change than it looks — each has its own default, and a URL
 * that omits a filter has to mean "the default" rather than "none" —
 * and doing it here would bury a filter slice inside a rewrite of every
 * filter bar. It is a follow-up, not a side effect.
 *
 * The consequence is honest and worth knowing: a shared link carries
 * the areas and not the status. That is strictly more than the zero
 * filters a link carried before.
 *
 * ## Push, not replace
 *
 * ``setSearchParams`` defaults to a history PUSH, and that is what is
 * wanted: the requirement is that Back restores the previous selection.
 * ``{replace: true}`` — which ``PortfolioMapSection`` uses for its
 * filters — would make Back leave the page instead, which is the
 * behaviour this slice is asked to avoid. The cost is that toggling
 * five areas leaves five history entries; that is the price of Back
 * meaning what people expect it to mean.
 *
 * ## The empty state has no representation
 *
 * No selection removes BOTH parameters rather than writing
 * ``activity_category=``. An empty-valued parameter is a third state
 * that looks like a selection to anything reading the string, and the
 * backend would have to decide whether it means "none" or "all".
 */
import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

/** Repeated key, matching every other multi-value param in the API. */
export const CATEGORY_PARAM = "activity_category";

/** Separate flag, never a magic member of the list above. */
export const UNASSIGNED_PARAM = "activity_category_unassigned";

/**
 * De-duplicate, drop blanks, keep first-seen order.
 *
 * Mirrors ``normalize_filter_keys`` on the server so the same URL means
 * the same thing on both sides — including the ordering, which is what
 * keeps a shared link byte-identical to the one that was copied.
 */
export function normalizeCategoryKeys(values) {
  const seen = new Set();
  const out = [];
  for (const raw of values || []) {
    if (raw == null) continue;
    const key = String(raw).trim();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(key);
  }
  return out;
}

/**
 * `{categories, unassigned, isActive, setFilter, clearFilter}`.
 *
 * `setFilter` takes the whole next selection rather than a toggle: the
 * URL is the state, so a partial update would need a read-modify-write
 * against a value that may already have changed.
 */
export default function useActivityCategoryFilterParams() {
  const [searchParams, setSearchParams] = useSearchParams();

  // `getAll` is the half of the URLSearchParams API this repo had no
  // reason to use before — every existing filter is single-valued.
  const categories = useMemo(
    () => normalizeCategoryKeys(searchParams.getAll(CATEGORY_PARAM)),
    [searchParams]
  );

  const unassigned = searchParams.get(UNASSIGNED_PARAM) === "true";

  const setFilter = useCallback(
    (nextCategories, nextUnassigned) => {
      const keys = normalizeCategoryKeys(nextCategories);
      const wantsUnassigned = Boolean(nextUnassigned);
      setSearchParams((prev) => {
        // Built from `prev` so every other parameter survives — most
        // importantly `mode=reports`, without which changing the filter
        // would drop the user back into the calendar.
        const next = new URLSearchParams(prev);
        next.delete(CATEGORY_PARAM);
        next.delete(UNASSIGNED_PARAM);
        for (const key of keys) next.append(CATEGORY_PARAM, key);
        if (wantsUnassigned) next.set(UNASSIGNED_PARAM, "true");
        return next;
      });
    },
    [setSearchParams]
  );

  const clearFilter = useCallback(() => setFilter([], false), [setFilter]);

  return {
    categories,
    unassigned,
    // Whether anything is narrowed. Named rather than derived at each
    // call site so "no filter" means the same thing in the empty state,
    // the reset button and the request builder.
    isActive: categories.length > 0 || unassigned,
    setFilter,
    clearFilter,
  };
}

/**
 * The two request parameters for an API client, or `{}` when inert.
 *
 * Kept beside the hook because the wire names and the URL names are
 * deliberately identical — one place to read when checking that claim.
 * Callers spread it into their existing params object.
 */
export function activityCategoryRequestParams({ categories, unassigned }) {
  const params = {};
  if (categories && categories.length) params[CATEGORY_PARAM] = categories;
  if (unassigned) params[UNASSIGNED_PARAM] = true;
  return params;
}
